/**
 * 画面の一覧（03 6.1・6.2）。パスは仕様書 7 章の URL のまま（FE-D-021）、パスの変数名は API の主キーの物理名（FE-D-022）。
 * ①で SCR-002（ログイン）・SCR-075（404）・ERR-403、②で顧客系（SCR-005〜017 のうち 011・012 を除く・SCR-078）、
 * ③で案件・取引・活動・カレンダー（SCR-018〜024・SCR-035〜038）、④で目標・レポート（SCR-025〜034）と SCR-005 の集計を作った。
 * まだ作っていない画面は「準備中」の仮ページ（phase は作るフェーズ）。画面の部品は routes.tsx の SCREEN_PAGES。
 */
export interface ScreenDefinition {
  screen_id: string;
  path: string;
  title: string;
  allOf?: readonly string[];
  anyOf?: readonly string[];
  /** 作るフェーズ（② 顧客系 … ⑦ 設定・利用者/ロール画面・監査） */
  phase: '②' | '③' | '④' | '⑤' | '⑥' | '⑦';
}

/** 転送だけの画面（SCR-006・014・061） */
export const REDIRECT_SCREENS = [
  { screen_id: 'SCR-006', path: '/companies', to: '/customers?customer_type=company' },
  { screen_id: 'SCR-014', path: '/individuals', to: '/customers?customer_type=soleProprietor' },
  { screen_id: 'SCR-061', path: '/forms/new', to: '/forms/new/embed' },
] as const;

/** シェルの中に出す社内の画面（6.2 の RequireAuth の子。並びは 6.2 のまま） */
export const SHELL_SCREENS: readonly ScreenDefinition[] = [
  // SCR-005 は②で企業・個人事業主の部分を作り、④で API-REPT-01（案件・取引・目標の集計）に切り替えた
  { screen_id: 'SCR-005', path: '/', title: 'ダッシュボード', allOf: ['dashboard:read'], phase: '④' },
  { screen_id: 'SCR-007', path: '/companies/:customer_code', title: '企業詳細', allOf: ['companies:read'], phase: '②' },
  {
    screen_id: 'SCR-008',
    path: '/companies/:customer_code/edit',
    title: '企業情報編集',
    allOf: ['companies:update'],
    phase: '②',
  },
  { screen_id: 'SCR-009', path: '/companies/new', title: '企業情報登録', allOf: ['companies:create'], phase: '②' },
  { screen_id: 'SCR-010', path: '/customers', title: '顧客管理', allOf: ['customers:read'], phase: '②' },
  {
    screen_id: 'SCR-011',
    path: '/customers/export',
    title: '顧客CSV出力設定',
    allOf: ['customers:export'],
    phase: '⑦',
  },
  {
    screen_id: 'SCR-012',
    path: '/customers/import',
    title: '顧客データインポート',
    allOf: ['customers:import'],
    phase: '⑦',
  },
  {
    screen_id: 'SCR-013',
    path: '/customers/new',
    title: '顧客登録',
    anyOf: ['companies:create', 'sole_proprietors:create'],
    phase: '②',
  },
  {
    screen_id: 'SCR-015',
    path: '/individuals/:customer_code',
    title: '個人事業主詳細',
    allOf: ['sole_proprietors:read'],
    phase: '②',
  },
  {
    screen_id: 'SCR-016',
    path: '/individuals/:customer_code/edit',
    title: '個人事業主情報編集',
    allOf: ['sole_proprietors:update'],
    phase: '②',
  },
  {
    screen_id: 'SCR-017',
    path: '/individuals/new',
    title: '個人事業主情報登録',
    allOf: ['sole_proprietors:create'],
    phase: '②',
  },
  { screen_id: 'SCR-018', path: '/activities', title: '活動履歴一覧', allOf: ['activities:read'], phase: '③' },
  {
    screen_id: 'SCR-019',
    path: '/activities/:activity_code',
    title: '商談詳細',
    allOf: ['activities:read'],
    phase: '③',
  },
  { screen_id: 'SCR-020', path: '/calendar', title: 'カレンダー', allOf: ['calendar:read'], phase: '③' },
  { screen_id: 'SCR-021', path: '/deals', title: '案件管理', allOf: ['cases:read'], phase: '③' },
  { screen_id: 'SCR-022', path: '/deals/:case_code', title: '案件詳細', allOf: ['cases:read'], phase: '③' },
  { screen_id: 'SCR-023', path: '/deals/:case_code/edit', title: '新規案件登録', allOf: ['cases:update'], phase: '③' },
  { screen_id: 'SCR-024', path: '/deals/new', title: '新規案件登録', allOf: ['cases:create'], phase: '③' },
  { screen_id: 'SCR-025', path: '/goals', title: '目標管理', allOf: ['goals:read'], phase: '④' },
  { screen_id: 'SCR-026', path: '/goals/:goal_code', title: '目標値情報', allOf: ['goals:read'], phase: '④' },
  { screen_id: 'SCR-027', path: '/goals/:goal_code/edit', title: '目標編集', allOf: ['goals:update'], phase: '④' },
  { screen_id: 'SCR-028', path: '/goals/new', title: '新規目標', allOf: ['goals:create'], phase: '④' },
  { screen_id: 'SCR-029', path: '/reports', title: 'レポート', allOf: ['reports:read'], phase: '④' },
  {
    screen_id: 'SCR-030',
    path: '/reports/builder',
    title: 'レポートビルダー',
    allOf: ['report_templates:create'],
    phase: '④',
  },
  {
    screen_id: 'SCR-031',
    path: '/reports/builder/:report_template_code',
    title: 'レポートビルダー',
    allOf: ['report_templates:update'],
    phase: '④',
  },
  { screen_id: 'SCR-032', path: '/reports/daily', title: '営業日報', allOf: ['reports:read'], phase: '④' },
  {
    screen_id: 'SCR-033',
    path: '/reports/templates',
    title: 'レポートテンプレート',
    allOf: ['report_templates:read'],
    phase: '④',
  },
  {
    screen_id: 'SCR-034',
    path: '/reports/user-performance',
    title: '担当者別パフォーマンス',
    allOf: ['reports:read'],
    phase: '④',
  },
  { screen_id: 'SCR-035', path: '/transactions', title: '取引履歴', allOf: ['transactions:read'], phase: '③' },
  {
    screen_id: 'SCR-036',
    path: '/transactions/:transaction_code',
    title: '取引詳細',
    allOf: ['transactions:read'],
    phase: '③',
  },
  {
    screen_id: 'SCR-037',
    path: '/transactions/:transaction_code/edit',
    title: '新規取引登録',
    allOf: ['transactions:update'],
    phase: '③',
  },
  {
    screen_id: 'SCR-038',
    path: '/transactions/new',
    title: '新規取引登録',
    allOf: ['transactions:create'],
    phase: '③',
  },
  {
    screen_id: 'SCR-039',
    path: '/checklist-templates',
    title: '項目設定',
    allOf: ['checklist_templates:read'],
    phase: '⑤',
  },
  {
    screen_id: 'SCR-040',
    path: '/checklist-templates/:checklist_template_code/edit',
    title: 'テンプレート編集',
    allOf: ['checklist_templates:update'],
    phase: '⑤',
  },
  {
    screen_id: 'SCR-040',
    path: '/checklist-templates/new',
    title: 'テンプレート編集',
    allOf: ['checklist_templates:create'],
    phase: '⑤',
  },
  {
    screen_id: 'SCR-041',
    path: '/checklists',
    title: '月次チェックリスト',
    allOf: ['monthly_checklists:read'],
    phase: '⑤',
  },
  {
    screen_id: 'SCR-042',
    path: '/checklists-matrix',
    title: '月次チェックリスト（マトリクス）',
    allOf: ['monthly_checklists:read'],
    phase: '⑤',
  },
  {
    screen_id: 'SCR-043',
    path: '/checklists/:contract_code',
    title: 'チェックリスト詳細',
    allOf: ['monthly_checklists:read'],
    phase: '⑤',
  },
  {
    screen_id: 'SCR-044',
    path: '/checklists/import',
    title: 'チェックリスト取込',
    allOf: ['monthly_checklists:import'],
    phase: '⑦',
  },
  { screen_id: 'SCR-045', path: '/contracts', title: '契約一覧', allOf: ['contracts:read'], phase: '⑤' },
  { screen_id: 'SCR-046', path: '/contracts/:contract_code', title: '契約詳細', allOf: ['contracts:read'], phase: '⑤' },
  {
    screen_id: 'SCR-047',
    path: '/contracts/:contract_code/edit',
    title: '契約編集',
    allOf: ['contracts:update'],
    phase: '⑤',
  },
  {
    screen_id: 'SCR-048',
    path: '/contracts/:contract_code/price-change',
    title: '単価変更',
    allOf: ['contracts:update'],
    phase: '⑤',
  },
  { screen_id: 'SCR-049', path: '/contracts/import', title: '契約データ取込', allOf: ['contracts:import'], phase: '⑦' },
  { screen_id: 'SCR-050', path: '/contracts/new', title: '契約登録', allOf: ['contracts:create'], phase: '⑤' },
  { screen_id: 'SCR-051', path: '/personnel', title: '要員一覧', allOf: ['personnel:read'], phase: '⑤' },
  {
    screen_id: 'SCR-052',
    path: '/personnel/:personnel_code',
    title: '要員詳細',
    allOf: ['personnel:read'],
    phase: '⑤',
  },
  {
    screen_id: 'SCR-053',
    path: '/personnel/:personnel_code/edit',
    title: '要員編集',
    allOf: ['personnel:update'],
    phase: '⑤',
  },
  { screen_id: 'SCR-054', path: '/personnel/import', title: '要員データ取込', allOf: ['personnel:import'], phase: '⑦' },
  { screen_id: 'SCR-055', path: '/personnel/new', title: '要員登録', allOf: ['personnel:create'], phase: '⑤' },
  {
    screen_id: 'SCR-056',
    path: '/chat-groups',
    title: 'メッセージを選択',
    anyOf: ['contact_threads:read', 'form_submissions:read'],
    phase: '⑥',
  },
  { screen_id: 'SCR-058', path: '/forms', title: 'フォーム管理', allOf: ['forms:read'], phase: '⑥' },
  { screen_id: 'SCR-059', path: '/forms/:form_code', title: 'フォーム詳細', allOf: ['forms:read'], phase: '⑥' },
  { screen_id: 'SCR-060', path: '/forms/:form_code/edit', title: 'フォーム編集', allOf: ['forms:update'], phase: '⑥' },
  { screen_id: 'SCR-062', path: '/forms/new/:mode', title: '新規フォーム作成', allOf: ['forms:create'], phase: '⑥' },
  { screen_id: 'SCR-063', path: '/submissions', title: 'フォーム回答', allOf: ['form_submissions:read'], phase: '⑥' },
  {
    screen_id: 'SCR-064',
    path: '/submissions/:form_submission_code',
    title: 'フォーム回答詳細',
    allOf: ['form_submissions:read'],
    phase: '⑥',
  },
  { screen_id: 'SCR-065', path: '/audit-logs', title: '監査ログ詳細', allOf: ['audit_logs:read'], phase: '⑦' },
  { screen_id: 'SCR-066', path: '/mypage', title: 'マイページ', phase: '⑦' },
  { screen_id: 'SCR-067', path: '/settings/company', title: '企業設定', allOf: ['system_settings:update'], phase: '⑦' },
  {
    screen_id: 'SCR-068',
    path: '/settings/numbering-rules',
    title: '採番ルール設定',
    allOf: ['numbering_rules:update'],
    phase: '⑦',
  },
  { screen_id: 'SCR-069', path: '/settings/roles', title: 'ロール管理', allOf: ['roles:update'], phase: '⑦' },
  {
    screen_id: 'SCR-070',
    path: '/settings/roles/:role_code/edit',
    title: 'ロール編集',
    allOf: ['roles:update'],
    phase: '⑦',
  },
  { screen_id: 'SCR-071', path: '/settings/roles/new', title: '新規ロール作成', allOf: ['roles:create'], phase: '⑦' },
  { screen_id: 'SCR-072', path: '/users', title: 'ユーザー管理', allOf: ['users:update'], phase: '⑦' },
  { screen_id: 'SCR-073', path: '/users/:user_code/edit', title: 'ユーザー編集', allOf: ['users:update'], phase: '⑦' },
  { screen_id: 'SCR-074', path: '/users/new', title: '新規ユーザー登録', allOf: ['users:create'], phase: '⑦' },
  { screen_id: 'SCR-078', path: '/tags', title: 'タグ管理', allOf: ['tags:read'], phase: '②' },
];
